/**
 * Product Library — drag-to-reorder + inline cover replace, visibility
 * toggle, and schedule modal triggers. The parent handles the actual API
 * mutations; this component is purely view + drag-state machinery.
 */
import { useEffect, useState } from "react";
import {
  Image as ImageIcon,
  GripVertical,
  Eye,
  EyeOff,
  CalendarClock,
  Loader2,
} from "lucide-react";
import { VisibilityBadge } from "./VisibilityBadge";

export const ProductLibraryTable = ({
  products,
  onEditImage,
  onSaveOrder,
  onToggleVisibility,
  onScheduleProduct,
}) => {
  // Local ordered copy of products so drag-reordering feels instant.
  const [items, setItems] = useState(products);
  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);
  const [saving, setSaving] = useState(false);

  // Sync if parent products change (e.g. after `load()` refresh).
  useEffect(() => {
    setItems(products);
  }, [products]);

  const dirty = items.length === products.length &&
    items.some((p, i) => p.id !== products[i]?.id);

  const handleDragStart = (e, idx) => {
    setDragIndex(idx);
    e.dataTransfer.effectAllowed = "move";
    // Firefox requires data to be set on dataTransfer for drag to start.
    e.dataTransfer.setData("text/plain", String(idx));
  };

  const handleDragOver = (e, idx) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (idx !== overIndex) setOverIndex(idx);
  };

  const handleDrop = (e, dropIdx) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === dropIdx) {
      setDragIndex(null); setOverIndex(null);
      return;
    }
    const next = [...items];
    const [moved] = next.splice(dragIndex, 1);
    next.splice(dropIdx, 0, moved);
    setItems(next);
    setDragIndex(null);
    setOverIndex(null);
  };

  const handleDragEnd = () => {
    setDragIndex(null);
    setOverIndex(null);
  };

  const handleApply = async () => {
    setSaving(true);
    try {
      await onSaveOrder(items.map((p) => p.id));
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => setItems(products);

  return (
    <div className="bg-white border border-slate-200" data-testid="product-library-section">
      <div className="px-5 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <ImageIcon size={14} className="text-[#C5A059]" />
          <span className="font-accent text-[11px] tracking-wider text-[#0A192F]">
            PRODUCT LIBRARY ({items.length})
          </span>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[10px] text-slate-400 hidden sm:block">
            Drag the <GripVertical size={10} className="inline -mt-0.5" /> handle to reorder · Click REPLACE to swap a cover.
          </span>
          {dirty && (
            <div className="flex items-center gap-2" data-testid="reorder-dirty-actions">
              <button
                data-testid="reorder-reset-btn"
                onClick={handleReset}
                className="text-[10px] font-accent text-slate-500 hover:text-[#0A192F] px-2 py-1"
              >
                RESET
              </button>
              <button
                data-testid="reorder-apply-btn"
                onClick={handleApply}
                disabled={saving}
                className="text-[10px] font-accent text-white bg-[#C5A059] hover:bg-[#C5A059]/90 disabled:opacity-50 px-3 py-1.5 inline-flex items-center gap-1.5"
              >
                {saving ? (
                  <><Loader2 size={10} className="animate-spin" /> SAVING…</>
                ) : (
                  <>APPLY ORDER</>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
      {items.length === 0 ? (
        <div className="px-5 py-8 text-center text-slate-400 text-xs">
          No products loaded.
        </div>
      ) : (
        <table className="w-full text-sm">
          <thead className="text-slate-500">
            <tr className="text-left">
              <th className="w-8"></th>
              <th className="px-2 py-2 font-accent text-[10px] w-20">COVER</th>
              <th className="px-5 py-2 font-accent text-[10px]">PRODUCT</th>
              <th className="px-5 py-2 font-accent text-[10px] hidden md:table-cell">IMAGE URL</th>
              <th className="px-5 py-2 font-accent text-[10px] w-44 text-right">ACTIONS</th>
            </tr>
          </thead>
          <tbody>
            {items.map((p, idx) => (
              <tr
                key={p.id}
                data-testid={`product-library-row-${p.id}`}
                draggable
                onDragStart={(e) => handleDragStart(e, idx)}
                onDragOver={(e) => handleDragOver(e, idx)}
                onDrop={(e) => handleDrop(e, idx)}
                onDragEnd={handleDragEnd}
                className={`border-t border-slate-100 align-middle transition-colors ${
                  dragIndex === idx ? "opacity-40" : ""
                } ${overIndex === idx && dragIndex !== idx ? "bg-[#FBF8F1]" : ""}`}
              >
                <td className="pl-3 py-3 cursor-grab active:cursor-grabbing text-slate-300 hover:text-[#C5A059]">
                  <GripVertical size={16} data-testid={`drag-handle-${p.id}`} />
                </td>
                <td className="px-2 py-3">
                  {p.image_url ? (
                    <img
                      src={p.image_url}
                      alt={p.name}
                      className="w-12 h-16 object-contain bg-[#0A192F] border border-slate-200"
                    />
                  ) : (
                    <div className="w-12 h-16 bg-slate-100 border border-dashed border-slate-300 flex items-center justify-center">
                      <ImageIcon size={14} className="text-slate-400" />
                    </div>
                  )}
                </td>
                <td className="px-5 py-3">
                  <div className="font-display text-sm text-[#0A192F]">{p.name}</div>
                  <div className="text-[10px] font-mono text-slate-400 mt-0.5">{p.id}</div>
                  <VisibilityBadge status={p.visibility_status} product={p} />
                </td>
                <td className="px-5 py-3 hidden md:table-cell">
                  <code className="text-[10px] text-slate-500 break-all line-clamp-2">
                    {p.image_url || "—"}
                  </code>
                </td>
                <td className="px-5 py-3 text-right">
                  <div className="inline-flex items-center gap-1.5">
                    <button
                      data-testid={`visibility-toggle-${p.id}`}
                      onClick={() => onToggleVisibility?.(p)}
                      title={p.visibility_status === "hidden" ? "Show on storefront" : "Hide from storefront"}
                      className="p-1.5 text-slate-500 hover:text-[#0A192F] border border-slate-200 hover:border-[#0A192F]"
                    >
                      {p.visibility_status === "hidden" ? <EyeOff size={12} /> : <Eye size={12} />}
                    </button>
                    <button
                      data-testid={`schedule-btn-${p.id}`}
                      onClick={() => onScheduleProduct?.(p)}
                      title="Schedule launch / archive"
                      className="p-1.5 text-slate-500 hover:text-[#0A192F] border border-slate-200 hover:border-[#0A192F]"
                    >
                      <CalendarClock size={12} />
                    </button>
                    <button
                      data-testid={`replace-image-btn-${p.id}`}
                      onClick={() => onEditImage(p)}
                      className="px-3 py-1.5 bg-[#0A192F] text-white text-[11px] font-accent hover:bg-[#0A192F]/90 inline-flex items-center gap-1.5"
                    >
                      <ImageIcon size={11} /> REPLACE
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};
